import { useState, useEffect } from 'react';
import { IQuestionnaire } from '../types/questionnaire';
import { FormBuilderService } from '../api/FormBuilderService';

export const useQuestionnaireManagement = (companyId: number | null, userId: number | null) => {
  const [questionnaires, setQuestionnaires] = useState<IQuestionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchQuestionnaires = async () => {
      try {
        if (!companyId) {
          setError('Company ID not found');
          setLoading(false);
          return;
        }
        const response = await FormBuilderService.getQuestionnairesByCompanyId(companyId.toString());
        setQuestionnaires(response);
        setLoading(false);
      } catch (err: any) {
        if(err.status !== 404){
          console.error("Error fetching questionnaires:", err);
        }else{
          console.warn("No questionnaires found for this company");
        }
        setLoading(false);
      }
    };

    fetchQuestionnaires();
  }, [companyId, userId]);

  const handleStatusChange = async (questionnaireId: string, newStatus: boolean) => {
    try {
      if (!companyId) {
        setError('Company ID not found');
        return;
      }

      if(newStatus) {
        await FormBuilderService.patchQuestionnaire(questionnaireId, { active: true });
      } else {
        await FormBuilderService.deactivateQuestionnaireStatus(questionnaireId, newStatus, companyId.toString());
      }

      // Update the local state to reflect the change
      // When deactivating, also clear isDefault since the API auto-unsets it
      setQuestionnaires(prevQuestionnaires => 
        prevQuestionnaires.map(q => 
          q.questionnaireId === questionnaireId 
            ? { ...q, active: newStatus, ...(newStatus === false ? { isDefault: false } : {}) }
            : q
        )
      );
    } catch (err) {
      console.error('Error updating questionnaire status:', err);
      setError('Failed to update questionnaire status');
    }
  };

  const handleSetDefault = async (questionnaireId: string) => {
    try {
      const updated = await FormBuilderService.setDefaultQuestionnaire(questionnaireId);

      // Update local state: set isDefault true + defaultedAt on target, clear on all others
      setQuestionnaires(prevQuestionnaires =>
        prevQuestionnaires.map(q => ({
          ...q,
          isDefault: q.questionnaireId === questionnaireId,
          defaultedAt: q.questionnaireId === questionnaireId ? updated.defaultedAt : undefined
        }))
      );
    } catch (err) {
      console.error('Error setting default questionnaire:', err);
      setError('Failed to set default questionnaire');
    }
  };

  const handleRemoveDefault = async (questionnaireId: string) => {
    try {
      await FormBuilderService.removeDefaultQuestionnaire(questionnaireId);

      // Update local state: clear isDefault and defaultedAt on target
      setQuestionnaires(prevQuestionnaires =>
        prevQuestionnaires.map(q =>
          q.questionnaireId === questionnaireId
            ? { ...q, isDefault: false, defaultedAt: undefined }
            : q
        )
      );
    } catch (err) {
      console.error('Error removing default questionnaire:', err);
      setError('Failed to remove default questionnaire');
    }
  };

  return {
    questionnaires,
    loading,
    error,
    setError,
    handleStatusChange,
    handleSetDefault,
    handleRemoveDefault
  };
}; 